
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';

import { ReportsActions } from '@actions/index'
import * as fromReports from '@reducers/index'
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { RequestModel, ReportSettingsModel, RatingPerLocationModel, StarsDistributionPerLocationModel, ReviewCountPerLocationModel, FeedbackCountPerLocationModel, SendEngineEfficiencyPerLocationModel, AvergeContactSpeedPerLocationModel, FeedbackReviewPercentagePerLocationModel, ReviewPerReviewSitePerLocationModel, SentimentCheckCountPerLocationModel, CampaignMetricsPerLocationModel, ClickedOnReviewPerLocationModel, GeneratedReportsModel, ScheduledReportModel } from '@models/index';
import { RatingPerReviewSitePerLocationModel } from '@models/ratingPerReviewSitePerLocation.model';
import { SentReviewRequestCountPerLocationModel } from '@models/sentReviewRequestCountPerLocation.model';
import { map, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class ReportsFacadeService {

    constructor(private store: Store<any>, public router: Router, private http: HttpClient) { }

    getTotalGeneratedReports$(): Observable<number> {
        return this.store.select(fromReports.getTotalGeneratedReports)
    }

    getReportsSettingsByBusiness$(): Observable<ReportSettingsModel> {
        return this.store.select(fromReports.getReportsSettingsByBusiness)
    }

    getGeneratedReports$(): Observable<GeneratedReportsModel[]> {
        return this.store.select(fromReports.getGeneratedReports)
    }

    getRatingPerLocation$(): Observable<RatingPerLocationModel[]> {
        return this.store.select(fromReports.getRatingPerLocation)
    }

    getStarCountPerLocation$(): Observable<StarsDistributionPerLocationModel[]> {
        return this.store.select(fromReports.getStarsCountPerLocation)
    }

    getReviewCountPerLocation$(): Observable<ReviewCountPerLocationModel[]> {
        return this.store.select(fromReports.getReviewCountPerLocation)
    }

    getFeedbackCountPerLocation$(): Observable<FeedbackCountPerLocationModel[]> {
        return this.store.select(fromReports.getFeedbackCountPerLocation)
    }

    getClickedOnReviewCountPerLocation$(): Observable<ClickedOnReviewPerLocationModel[]> {
        return this.store.select(fromReports.getClickedOnReviewCountPerLocation)
    }

    getSendEngineEfficiencyPerLocation$(): Observable<SendEngineEfficiencyPerLocationModel[]> {
        return this.store.select(fromReports.getSendEngineEfficiencyPerLocation)
    }

    getAverageContactSpeedPerLocation$(): Observable<AvergeContactSpeedPerLocationModel[]> {
        return this.store.select(fromReports.getAverageContactSpeedPerLocation)
    }

    getFeedbackReviewPercentagePerLocation$(): Observable<FeedbackReviewPercentagePerLocationModel[]> {
        return this.store.select(fromReports.getFeedbackReviewPercentagePerLocation)
    }

    getReviewsPerReviewSitePerLocation$(): Observable<ReviewPerReviewSitePerLocationModel[]> {
        return this.store.select(fromReports.getReviewsPerReviewSitePerLocation)
    }

    getRatingPerReviewSitePerLocation$(): Observable<RatingPerReviewSitePerLocationModel[]> {
        return this.store.select(fromReports.getRatingPerReviewSitePerLocation)
    }

    getSentRequestCountPerLocation$(): Observable<SentReviewRequestCountPerLocationModel[]> {
        return this.store.select(fromReports.getSentRequestCountPerLocation)
    }

    getSentimentCheckCountPerLocation$(): Observable<SentimentCheckCountPerLocationModel[]> {
        return this.store.select(fromReports.getSentimentCheckCountPerLocation)
    }

    getCampaignMetricsPerLocation$(): Observable<CampaignMetricsPerLocationModel[]> {
        return this.store.select(fromReports.getCampaignsMetricsPerLocation)
    }

    getGeneralReportDateRange$(): Observable<{ startTime: number, endTime: number }> {
        return this.store.select(fromReports.getGeneralReportDateRange)
    }

    getReportsError$(): Observable<string> {
        return this.store.select(fromReports.getErrorReports)
    }

    getScheduledReports$(): Observable<ScheduledReportModel[]> {
        return this.store.select(fromReports.getScheduledReports)
    }

    getReportsSettingsByBusiness() {
        const request: RequestModel = {
            params: `business-report?`,
        }
        this.store.dispatch(ReportsActions.getReportsSettingsByBusiness({ request }))
    }

    saveReportsSettingsByBusiness(reportsSettings: ReportSettingsModel) {
        const request: RequestModel = {
            params: `business-report?updatedReport=${reportsSettings.updatedReport}`,
            payload: reportsSettings
        }
        this.store.dispatch(ReportsActions.saveReportsSettingsByBusiness({ request }))
    }

    generateReport(newReportData: GeneratedReportsModel) {
        const request: RequestModel = {
            params: `dashboard-report`,
            payload: newReportData
        }
        this.store.dispatch(ReportsActions.generateReport({ request }))
    }

    deleteGeneratedReport(deleteGeneratedReportId: string) {
        const request: RequestModel = {
            params: `dashboard-report/delete?reportId=${deleteGeneratedReportId}`
        }
        this.store.dispatch(ReportsActions.deleteGeneratedReport({ request }))
    }

    getRatingPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getRatingPerLocation(payload))
    }

    getStarsCountPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getStarsCountPerLocation(payload))
    }

    getReviewsPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getReviewsPerLocation(payload))
    }

    getFeedbacksPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getFeedbacksPerLocation(payload))
    }

    getClickedOnReviewPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getClickedOnReviewPerLocation(payload))
    }

    getSendEngineEfficiencyPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getSendEngineEfficiencyPerLocation(payload))
    }

    getAverageContactSpeedPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getAverageContactSpeedPerLocation(payload))
    }

    getReviewPerReviewSitePerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getReviewPerReviewSitePerLocation(payload))
    }

    getRatingPerReviewSitePerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getRatingPerReviewSitePerLocation(payload))
    }

    getSentRequestCountPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getSentRequestCountPerLocation(payload))
    }

    getSentimentCheckCountPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getSentimentCheckCountPerLocation(payload))
    }

    getCampaignMetricsPerLocation(payload: { startDate: number, endDate: number }) {
        this.store.dispatch(ReportsActions.getCampaignMetricsPerLocation(payload))
    }

    setGeneralReportDateRange(payload: { startTime: number, endTime: number }) {
        this.store.dispatch(ReportsActions.setGeneralReportDateRange({ payload }))
    }

    sendTest(payload: { businessId: string, email: string }) {
        const request: RequestModel = {
            params: `business-report/generate-test-report?businessId=${payload.businessId}&userEmail=${payload.email}`
        }
        this.store.dispatch(ReportsActions.sendTest({ request }))
    }

    searchGeneratedReports(pages: { page: number, size: number, businessId: string }) {
        return this.store.dispatch(ReportsActions.searchGeneratedReports({ pages }))
    }

    searchGeneratedReportsTotal(payload: { page: number, size: number, businessId: string }): Observable<{ data: GeneratedReportsModel[], total: number }> {
        return this.http
            .get(`${environment.apiPath}/dashboard-report?page=${payload.page}&size=${payload.size}&businessId=${payload.businessId}`)
            .pipe(
                map((items: { data: GeneratedReportsModel[], total: number }) => ({
                    data: items.data,
                    total: items.total
                }))
            )
    }

    searchScheduledReportsPerBusiness(businessId: string) {
        const request: RequestModel = {
            params: `scheduled-report?businessId=${businessId}`
        }
        this.store.dispatch(ReportsActions.searchScheduledReportsPerBusiness({ request }))
    }

    addScheduledReportToBusiness(newScheduledReport: ScheduledReportModel) {
        const request: RequestModel = {
            params: `scheduled-report`,
            payload: newScheduledReport
        }
        this.store.dispatch(ReportsActions.addScheduledReportToBusiness({ request }))
    }

    editScheduledReport(editedScheduledReport: ScheduledReportModel) {
        const request: RequestModel = {
            params: `scheduled-report/update`,
            payload: editedScheduledReport
        }
        this.store.dispatch(ReportsActions.editScheduledReport({ request }))
    }

    deleteScheduledReport(scheduledReportId: string) {
        const request: RequestModel = {
            params: `scheduled-report/delete?id=${scheduledReportId}`
        }
        this.store.dispatch(ReportsActions.deleteScheduledReport({ request }))
    }

    clearReportSettings() {
        this.store.dispatch(ReportsActions.clearReportSettings())
    }

    getClickedOnReviewForLocationCampaign(date: { startDate: number, endDate: number }, campaignId: string, locationId: string) {
        this.store.dispatch(ReportsActions.getClickedOnReviewForLocationCampaign({ ...date, campaignId, locationId }))
    }

    getSendEngineEfficiencyForLocationCampaign(date: { startDate: number, endDate: number }, campaignId: string, locationId: string) {
        this.store.dispatch(ReportsActions.getSendEngineEfficiencyForLocationCampaign({ ...date, campaignId, locationId }))
    }

    getSentRequestCountForLocationCampaign(date: { startDate: number, endDate: number }, campaignId: string, locationId: string) {
        this.store.dispatch(ReportsActions.getSentRequestCountForLocationCampaign({ ...date, campaignId, locationId }))
    }

    // TODO: unused??? (the action has no effect!)
    getSentimentCheckCountForLocationCampaign(date: { startDate: number, endDate: number }, campaignId: string, locationId: string) {
        const request: RequestModel = {
            params: `business-report/location/stars-thumbs-faces-count?startTime=${date.startDate}&endTime=${date.endDate}&campaignId=${campaignId}&locationId=${locationId}`,
            payload: date
        }
        this.store.dispatch(ReportsActions.getSentimentCheckCountForLocationCampaign({ request }))
    }

    getCampaignMetricsForLocationCampaign(date: { startDate: number, endDate: number }, campaignId: string, locationId: string) {
        this.store.dispatch(ReportsActions.getCampaignMetricsForLocationCampaign({ ...date, campaignId, locationId }))
    }

    getFeedbacksPerLocationCampaign(date: { startDate: number, endDate: number }, campaignId: string, locationId: string) {
        this.store.dispatch(ReportsActions.getFeedbacksPerLocationCampaign({ ...date, campaignId, locationId }))
    }
}
